import { RetroTemplate } from './types';

/** Default canvas centre — templates are anchored here unless overridden. */
export const DEFAULT_TEMPLATE_ANCHOR = { x: 10000, y: 10000 };

/**
 * Hand-drawn pencil sketch — "What Went Well / What Didn't / Action Items".
 *
 * Asset: pencil-sketch.png (PNG RGBA 2560×1440, black ink on transparent bg).
 * Canvas size: 2× the source image so strokes stay crisp at typical zoom levels.
 *
 * Zone boundaries derived by scanning long ink strokes in the alpha channel:
 *   vertical dividers at image-px x≈898 and x≈1729
 *   zones span the full height — no separate title row in this version.
 *
 * Canvas scale: 1.3× the source image (down from the original 2×) so the
 * template fits comfortably alongside standard sticky notes at default zoom.
 */
const SCALE = 1.3;
const PENCIL_W = Math.round(2560 * SCALE); // 3328
const PENCIL_H = Math.round(1440 * SCALE); // 1872
const PENCIL_DIV1 = Math.round(898 * SCALE);  // 1167
const PENCIL_DIV2 = Math.round(1729 * SCALE); // 2248

export const wentWellTemplate: RetroTemplate = {
  id: 'went-well',
  name: 'What Went Well / What Didn\'t / Action Items',
  tagline: 'The universal baseline — works for every team, every sprint.',
  description: 'Half positive reinforcement, half honest reflection, and one column that forces a commitment to change. Also known as Keep–Drop–Try, Plus/Delta, and WWW/WDD.',
  accentLight: '#1565c0',
  accentDark: '#90caf9',
  comingSoon: true,
  anchor: DEFAULT_TEMPLATE_ANCHOR,
  size: { width: PENCIL_W, height: PENCIL_H },
  zones: [
    {
      id: 'went-well',
      title: 'Went Well',
      exportOrder: 1,
      shape: { kind: 'rect', x: 0, y: 0, width: PENCIL_DIV1, height: PENCIL_H },
    },
    {
      id: 'didnt-work',
      title: "Didn't Work",
      exportOrder: 2,
      shape: { kind: 'rect', x: PENCIL_DIV1, y: 0, width: PENCIL_DIV2 - PENCIL_DIV1, height: PENCIL_H },
    },
    {
      id: 'actions',
      title: 'Actions',
      exportOrder: 3,
      shape: { kind: 'rect', x: PENCIL_DIV2, y: 0, width: PENCIL_W - PENCIL_DIV2, height: PENCIL_H },
    },
  ],
};

/**
 * "Start / Stop / Continue" — behavior-focused retrospective.
 *
 * Asset: template_start_stop_continue.png (PNG RGBA 2560×1440, black ink on transparent bg).
 * Canvas scale: 1.3× the source image for crisp rendering and comfortable spacing with sticky notes.
 *
 * Zone boundaries: three equal columns representing the three categories.
 */
const SSC_SCALE = 1.3;
const SSC_W = Math.round(2560 * SSC_SCALE); // 3328
const SSC_H = Math.round(1440 * SSC_SCALE); // 1872
const SSC_DIV1 = Math.round((2560 / 3) * SSC_SCALE);  // 1109
const SSC_DIV2 = Math.round((2560 * 2 / 3) * SSC_SCALE); // 2218

export const startStopContinueTemplate: RetroTemplate = {
  id: 'start-stop-continue',
  name: 'Start / Stop / Continue',
  tagline: 'Behavior change — direct action on new habits and old patterns.',
  description: 'Every sticky note is inherently directional. Start what\'s missing, Stop what\'s not working, and Continue what\'s valuable. Naturally produces an actionable backlog.',
  accentLight: '#f57c00',
  accentDark: '#ffb74d',
  anchor: DEFAULT_TEMPLATE_ANCHOR,
  size: { width: SSC_W, height: SSC_H },
  zones: [
    {
      id: 'start',
      title: 'Start',
      exportOrder: 1,
      shape: { kind: 'rect', x: 0, y: 0, width: SSC_DIV1, height: SSC_H },
    },
    {
      id: 'stop',
      title: 'Stop',
      exportOrder: 2,
      shape: { kind: 'rect', x: SSC_DIV1, y: 0, width: SSC_DIV2 - SSC_DIV1, height: SSC_H },
    },
    {
      id: 'continue',
      title: 'Continue',
      exportOrder: 3,
      shape: { kind: 'rect', x: SSC_DIV2, y: 0, width: SSC_W - SSC_DIV2, height: SSC_H },
    },
  ],
};

/**
 * "Mad / Sad / Glad" — team health and emotional well-being retrospective.
 *
 * Asset: mad-sad-glad.png (PNG RGBA 2560×1440, black ink on transparent bg).
 * The frame (outer border, dividers, header separator) is pixel-identical to
 * pencil-sketch.png — only the header icons/wording differ — so it reuses the
 * exact same layout constants as wentWellTemplate.
 */
export const madSadGladTemplate: RetroTemplate = {
  id: 'mad-sad-glad',
  name: 'Mad / Sad / Glad',
  tagline: 'Team health check — makes feelings the primary data.',
  description: 'Deliberately surfaces emotions as data. Builds psychological safety by normalising how the team felt, not just what happened. Great after a hard sprint or as a counterbalance to analytical retros.',
  accentLight: '#c62828',
  accentDark: '#ef9a9a',
  comingSoon: true,
  anchor: DEFAULT_TEMPLATE_ANCHOR,
  size: { width: PENCIL_W, height: PENCIL_H },
  zones: [
    {
      id: 'mad',
      title: 'Mad',
      exportOrder: 1,
      shape: { kind: 'rect', x: 0, y: 0, width: PENCIL_DIV1, height: PENCIL_H },
    },
    {
      id: 'sad',
      title: 'Sad',
      exportOrder: 2,
      shape: { kind: 'rect', x: PENCIL_DIV1, y: 0, width: PENCIL_DIV2 - PENCIL_DIV1, height: PENCIL_H },
    },
    {
      id: 'glad',
      title: 'Glad',
      exportOrder: 3,
      shape: { kind: 'rect', x: PENCIL_DIV2, y: 0, width: PENCIL_W - PENCIL_DIV2, height: PENCIL_H },
    },
  ],
};

/**
 * "Circles of Control" — agency and focus retrospective.
 *
 * Asset: circles-of-control.png (PNG RGBA 2560×1440, black ink on transparent bg).
 * Canvas scale/size matches startStopContinueTemplate (1.3× the 2560×1440 source)
 * so it sits comfortably at the same footprint as the other wide templates.
 *
 * Three nested ellipses — Concern (outer), Influence (middle), Control (inner) —
 * stretched horizontally to suit widescreen monitors better than a plain circle.
 * Each zone is defined as a *full* ellipse rather than an explicit ring: overlapping
 * zones are resolved by findZoneForPoint's smallest-area-wins rule, so a point deep
 * inside the shape naturally resolves to "control", one further out to "influence",
 * and everything else to "concern" — no annulus math needed.
 */
const CIRCLES_SCALE = 1.3;
const CIRCLES_W = Math.round(2560 * CIRCLES_SCALE); // 3328
const CIRCLES_H = Math.round(1440 * CIRCLES_SCALE); // 1872
const CIRCLES_CX = Math.round(1280 * CIRCLES_SCALE); // 1664
const CIRCLES_CY = Math.round(720 * CIRCLES_SCALE);  // 936
const CONCERN_RX = Math.round(1200 * CIRCLES_SCALE); // 1560
const CONCERN_RY = Math.round(650 * CIRCLES_SCALE);  // 845
const INFLUENCE_RX = Math.round(800 * CIRCLES_SCALE); // 1040
const INFLUENCE_RY = Math.round(430 * CIRCLES_SCALE); // 559
const CONTROL_RX = Math.round(380 * CIRCLES_SCALE);   // 494
const CONTROL_RY = Math.round(205 * CIRCLES_SCALE);   // 267

export const circlesOfControlTemplate: RetroTemplate = {
  id: 'circles-of-control',
  name: 'Circles of Control',
  tagline: 'Agency and focus — spend energy where it can actually land.',
  description: 'Sorts what came up into what\'s outside anyone\'s control, what the team can influence, and what the team directly owns. Helps teams stop spending retro energy on things they can\'t change.',
  accentLight: '#00695c',
  accentDark: '#80cbc4',
  comingSoon: true,
  anchor: DEFAULT_TEMPLATE_ANCHOR,
  size: { width: CIRCLES_W, height: CIRCLES_H },
  zones: [
    {
      id: 'concern',
      title: 'Circle of Concern',
      exportOrder: 1,
      shape: { kind: 'circle', cx: CIRCLES_CX, cy: CIRCLES_CY, radius: CONCERN_RX, radiusY: CONCERN_RY },
    },
    {
      id: 'influence',
      title: 'Circle of Influence',
      exportOrder: 2,
      shape: { kind: 'circle', cx: CIRCLES_CX, cy: CIRCLES_CY, radius: INFLUENCE_RX, radiusY: INFLUENCE_RY },
    },
    {
      id: 'control',
      title: 'Circle of Control',
      exportOrder: 3,
      shape: { kind: 'circle', cx: CIRCLES_CX, cy: CIRCLES_CY, radius: CONTROL_RX, radiusY: CONTROL_RY },
    },
  ],
};

// Start / Stop / Continue is the only template whose layout has been fully
// polished, so it ships first and is the only one selectable in the picker.
// The rest are marked `comingSoon` — see "Template layout — release gate"
// in doc/retrospective/RETRO_ROOM_PLAN.md.
export const RETRO_TEMPLATES: RetroTemplate[] = [
  startStopContinueTemplate,
  wentWellTemplate,
  madSadGladTemplate,
  circlesOfControlTemplate,
];

export const RETRO_TEMPLATE_IDS = RETRO_TEMPLATES.map(t => t.id);
